import { useTranslation } from "react-i18next";
import React from "react";
import { Dialog, DialogActions, DialogBody, DialogTitle } from "src/components/base/dialog";
import { ErrorMessage, Field, FieldGroup, Fieldset, Label, RequiredLabel } from "src/components/base/fieldset";
import { Input } from "src/components/base/input";
import { Button, PrimaryButton } from "src/components/base/button";
import { useForm } from "@tanstack/react-form";
import { Checkbox, CheckboxField, CheckboxGroup } from "src/components/base/checkbox";
import Form from "src/components/base/form";
import { FullSplitDnsResolver } from "src/api/generated/frontend_internal_v1";
import { ApiInternal } from "src/api/api_internal";
import { toast } from "react-toastify";

/**
 * The properties for {@link UpdateResolverDialog}
 */
export type UpdateResolverDialogProps = {
    /** resolver to be updated */
    resolver: FullSplitDnsResolver;
    /** Callback for closing the dialog */
    onClose: () => void;
    /** Callback for submitting the form */
    onSubmit: () => void;
};

/**
 * A dialog for adding a new split dns domain
 */
export default function UpdateResolverDialog(props: UpdateResolverDialogProps) {
    const [t] = useTranslation("dialog-add-resolver");

    const form = useForm({
        defaultValues: {
            resolverIp: props.resolver.ip,
            description: props.resolver.description,
            disabled: props.resolver.disabled,
        },
        validators: {
            onSubmitAsync: async ({ value }) => {
                const res = await ApiInternal.dns.updateResolverSplitDns(props.resolver.uuid, {
                    ip: value.resolverIp,
                    description: value.description,
                    disabled: value.disabled,
                });

                if (res.result === "Err") {
                    return {
                        fields: {
                            resolverIp: res.error.ip_invalid
                                ? t("error.invalid-ip")
                                : res.error.ip_already_exists
                                  ? t("error.ip-already-exists")
                                  : undefined,
                        },
                    };
                }
            },
        },
        onSubmit: () => {
            toast.success(t("toast.updated-successfully"));
            props.onSubmit();
        },
    });

    return (
        <Dialog onClose={props.onClose} open={true}>
            <Form onSubmit={form.handleSubmit}>
                <DialogTitle>{t("heading.update-resolver")}</DialogTitle>
                <DialogBody>
                    <Fieldset>
                        <FieldGroup>
                            <form.Field name={"resolverIp"}>
                                {(fieldApi) => (
                                    <Field>
                                        <RequiredLabel>{t("label.ip")}</RequiredLabel>
                                        <Input
                                            autoFocus={true}
                                            required={true}
                                            value={fieldApi.state.value}
                                            onChange={(e) => fieldApi.handleChange(e.target.value)}
                                            invalid={fieldApi.state.meta.errors.length > 0}
                                        />
                                        {fieldApi.state.meta.errors.map((err) => (
                                            <ErrorMessage key={err}>{err}</ErrorMessage>
                                        ))}
                                    </Field>
                                )}
                            </form.Field>
                            <form.Field name={"description"}>
                                {(fieldApi) => (
                                    <Field>
                                        <Label>{t("label.description")}</Label>
                                        <Input
                                            maxLength={1024}
                                            value={fieldApi.state.value}
                                            onChange={(e) => fieldApi.handleChange(e.target.value)}
                                        />
                                    </Field>
                                )}
                            </form.Field>
                            <form.Field name={"disabled"}>
                                {(fieldApi) => (
                                    <Field>
                                        <Label>{t("label.disabled")}</Label>
                                        <CheckboxGroup>
                                            <CheckboxField>
                                                <Checkbox
                                                    checked={fieldApi.state.value}
                                                    onChange={(e) => fieldApi.handleChange(e)}
                                                />
                                                <Label>{t("description.disabled")}</Label>
                                            </CheckboxField>
                                        </CheckboxGroup>
                                    </Field>
                                )}
                            </form.Field>
                        </FieldGroup>
                    </Fieldset>
                </DialogBody>
                <DialogActions>
                    <Button onClick={props.onClose}>{t("button.cancel")}</Button>
                    <PrimaryButton type={"submit"}>{t("button.add")}</PrimaryButton>
                </DialogActions>
            </Form>
        </Dialog>
    );
}
